import { useState } from 'react';
import { BarChart3, UserX, UserMinus, CalendarDays, PiggyBank, LineChart, Scale } from 'lucide-react';
import IncomeReport from '../components/IncomeReport';
import InactiveReport from '../components/InactiveReport';
import OccupancyReport from '../components/OccupancyReport';
import ForecastReport from '../components/ForecastReport';
import LostReport from '../components/LostReport';
import EvolutionReport from '../components/EvolutionReport';
import RentabilidadClases from '../components/RentabilidadClases';
import { useAuth } from '../../../context/AuthContext';

type Tab = 'pronostico' | 'ingresos' | 'rentabilidad' | 'inactivos' | 'bajas' | 'ocupacion' | 'evolucion';

// B7 (20-oct-2026, Mabel #1484): «Punto de equilibrio y rentabilidad por clase». Apagado hasta que Carlos/Mabel revisen los
// números con la API ya reiniciada (GET /finances/presupuesto/rentabilidad). Encender = cambiar a `true` + ./deploy.sh.
// (La QA visual compila con VITE_LM_RENTABILIDAD=1 a una carpeta temporal; el deploy normal no lo define → oculta.)
const RENTABILIDAD_VISIBLE: boolean = true || import.meta.env.VITE_LM_RENTABILIDAD === '1';

// Cada reporte respeta el módulo de sus datos: si ese módulo está apagado, su tab no aparece.
const TABS: { key: Tab; label: string; short: string; icon: typeof BarChart3; mod: string }[] = [
  { key: 'pronostico', label: 'Pronóstico y presupuesto', short: 'Presupuesto', icon: PiggyBank, mod: 'letsmove_finances' },
  { key: 'ingresos', label: 'Ingresos', short: 'Ingresos', icon: BarChart3, mod: 'letsmove_finances' },
  { key: 'rentabilidad', label: 'Punto de equilibrio y rentabilidad por clase', short: 'Equilibrio', icon: Scale, mod: 'letsmove_finances' },
  { key: 'inactivos', label: 'Alumnos inactivos', short: 'Inactivos', icon: UserX, mod: 'letsmove_members' },
  { key: 'bajas', label: 'Bajas', short: 'Bajas', icon: UserMinus, mod: 'letsmove_members' },
  { key: 'ocupacion', label: 'Ocupación de clases', short: 'Ocupación', icon: CalendarDays, mod: 'letsmove_classes' },
  { key: 'evolucion', label: 'Evolución de clases', short: 'Evolución', icon: LineChart, mod: 'letsmove_classes' },
];

export default function ReportsPage() {
  const { hasModule } = useAuth();
  const [tab, setTab] = useState<Tab>('pronostico');
  // Solo tabs cuyos datos (su módulo) estén activos. Si el tab actual quedó fuera, cae en el 1º visible.
  const visibleTabs = TABS.filter((t) => hasModule(t.mod) && (t.key !== 'rentabilidad' || RENTABILIDAD_VISIBLE));
  const activeTab: Tab | undefined = visibleTabs.some((t) => t.key === tab) ? tab : visibleTabs[0]?.key;

  return (
    <div>
      <div className="mb-4">
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <BarChart3 className="text-brand-blue" size={24} /> Reportes
        </h1>
        <p className="mt-0.5 text-sm text-content-3">Los números del estudio para tomar decisiones.</p>
      </div>

      {visibleTabs.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface p-6 text-center text-sm text-content-3">
          No hay reportes disponibles. Activa Alumnos, Clases o Finanzas para ver sus números.
        </p>
      ) : (
        <>
          {/* Tabs */}
          <div className={`mb-5 grid ${visibleTabs.length > 6 ? 'grid-cols-4' : 'grid-cols-6'} gap-1 border-b border-line sm:flex sm:flex-wrap`}>
            {visibleTabs.map(({ key, label, short, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`-mb-px flex flex-col items-center justify-center gap-1 border-b-2 px-1 py-2 text-[10px] font-medium leading-tight transition sm:flex-row sm:gap-1.5 sm:px-3 sm:py-2.5 sm:text-sm ${
                  activeTab === key ? 'border-accent text-content' : 'border-transparent text-content-3 hover:text-content-2'}`}>
                <Icon size={16} className="shrink-0" />
                <span className="text-center sm:hidden">{short}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {activeTab === 'pronostico' && <ForecastReport />}
          {activeTab === 'ingresos' && <IncomeReport />}
          {activeTab === 'rentabilidad' && <RentabilidadClases />}
          {activeTab === 'inactivos' && <InactiveReport />}
          {activeTab === 'bajas' && <LostReport />}
          {activeTab === 'ocupacion' && <OccupancyReport />}
          {activeTab === 'evolucion' && <EvolutionReport />}
        </>
      )}
    </div>
  );
}
